import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  GridView as GridViewIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  RestartAlt as RestartAltIcon,
  Tune as TuneIcon,
  ViewAgenda as ViewAgendaIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { TableColumnSettings } from 'app/types';
import {
  ResolvedTableSettings,
  TABLE_COLUMN_LABELS,
  TABLE_PAGE_SIZE_OPTIONS,
  TableColumnKey,
} from 'components/tableSettings';
import {
  STATUS_META,
  pickStatusColor,
} from 'components/dictionaryView/statusMeta';
import { useTheme } from '@mui/material/styles';

type ViewMode = 'table' | 'cards';
type CardsPerRow = 1 | 2 | 3;

interface CardSettings {
  cardsPerRow: CardsPerRow;
  setCardsPerRow: (n: CardsPerRow) => void;
}

interface TableSettingsDialogProps {
  /**
   * The popover anchors to whichever element is passed here — typically the
   * gear button in DictionaryFilterBar. When `null`, the popover is closed.
   * Mirrors MUI Popover's `anchorEl` contract.
   */
  anchorEl: HTMLElement | null;
  settings: ResolvedTableSettings;
  /**
   * Affects only the header title ("Налаштування таблиці" / "Налаштування
   * карток"). The popover body is shared because column visibility / page
   * size / highlight apply to both modes today. Once card-specific
   * settings (cardsPerRow, showExample) land, gate them behind this flag.
   */
  viewMode?: ViewMode;
  /**
   * Card-only state. When `viewMode === 'cards'` and this is provided, the
   * popover renders the "Карток у рядку" + "Поля картки" sections in place
   * of the columnar drag list. Omitting it falls back to showing the shared
   * column-visibility list even in cards mode — fine for non-dictionary
   * consumers that don't manage a cards grid.
   */
  cards?: CardSettings;
  /** Optional override for the title — overrides the viewMode-based default. */
  title?: string;
  onClose: () => void;
  onReset: () => Promise<void> | void;
  onSave: (settings: ResolvedTableSettings) => Promise<void> | void;
}

const POPOVER_WIDTH = 380;

const GroupLabel: React.FC<{
  children: React.ReactNode;
  right?: React.ReactNode;
}> = ({ children, right }) => (
  <Stack
    direction={'row'}
    alignItems={'center'}
    justifyContent={'space-between'}
    sx={{ mb: 0.75, mx: 0.25, mt: 0.25 }}
  >
    <Typography
      sx={{
        color: 'text.secondary',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
    {right}
  </Stack>
);

const SquareCheckbox: React.FC<{
  checked: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ checked, disabled, onClick }) => (
  <Box
    onClick={(e) => {
      e.stopPropagation();
      if (!disabled) onClick();
    }}
    sx={{
      alignItems: 'center',
      bgcolor: checked ? 'primary.main' : 'transparent',
      border: '2px solid',
      borderColor: checked ? 'primary.main' : 'primary.tableBorder',
      borderRadius: '5px',
      cursor: disabled ? 'default' : 'pointer',
      display: 'inline-flex',
      flex: 'none',
      height: 20,
      justifyContent: 'center',
      opacity: disabled ? 0.5 : 1,
      transition: 'all .12s ease',
      width: 20,
    }}
  >
    {checked && (
      <Box
        component={'svg'}
        viewBox={'0 0 16 16'}
        sx={{ color: 'common.white', height: 14, width: 14 }}
      >
        <path
          d={'M3 8.5l3 3 7-7'}
          fill={'none'}
          stroke={'currentColor'}
          strokeWidth={2.4}
          strokeLinecap={'round'}
          strokeLinejoin={'round'}
        />
      </Box>
    )}
  </Box>
);

/**
 * Themed pill switch — accepts an arbitrary "active" colour (the prototype
 * paints the switch with the status colour so users connect the toggle to
 * what it actually controls).
 */
const StatusSwitch: React.FC<{
  on: boolean;
  color: string;
  onClick: () => void;
}> = ({ on, color, onClick }) => (
  <Box
    component={'button'}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    sx={(t) => ({
      background: on
        ? color
        : t.palette.mode === 'light'
        ? 'rgba(0,0,0,0.18)'
        : 'rgba(255,255,255,0.20)',
      border: 'none',
      borderRadius: 999,
      cursor: 'pointer',
      flex: 'none',
      height: 24,
      padding: '2px',
      position: 'relative',
      transition: 'background-color .16s ease',
      width: 40,
    })}
  >
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '50%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        height: 20,
        transform: on ? 'translateX(16px)' : 'translateX(0)',
        transition: 'transform .16s ease',
        width: 20,
      }}
    />
  </Box>
);

const ColumnRow: React.FC<{
  column: TableColumnSettings;
  index: number;
  total: number;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  draggable: boolean;
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
}> = ({
  column,
  index,
  total,
  onToggle,
  onMoveUp,
  onMoveDown,
  draggable,
  onPointerDown,
}) => {
  const label =
    TABLE_COLUMN_LABELS[column.key as TableColumnKey] ?? column.key;
  return (
    <Box
      data-table-column-key={column.key}
      onPointerDown={onPointerDown}
      sx={{
        alignItems: 'center',
        borderRadius: 1,
        cursor: draggable ? 'grab' : 'default',
        display: 'flex',
        gap: 1,
        height: 40,
        pl: 0.5,
        pr: 0.75,
        '&:hover': { bgcolor: 'background.default' },
      }}
    >
      <DragIndicatorIcon
        sx={{
          color: 'primary.tableBorder',
          flex: 'none',
          fontSize: 20,
        }}
      />
      <SquareCheckbox checked={column.visible} onClick={onToggle} />
      <Typography
        sx={{
          color: column.visible ? 'text.primary' : 'text.secondary',
          flex: 1,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <IconButton
        size={'small'}
        disabled={index === 0}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onMoveUp}
        sx={{
          color: 'text.secondary',
          height: 26,
          width: 26,
          '&.Mui-disabled': { opacity: 0.25 },
        }}
      >
        <KeyboardArrowUpIcon sx={{ fontSize: 19 }} />
      </IconButton>
      <IconButton
        size={'small'}
        disabled={index === total - 1}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onMoveDown}
        sx={{
          color: 'text.secondary',
          height: 26,
          width: 26,
          '&.Mui-disabled': { opacity: 0.25 },
        }}
      >
        <KeyboardArrowDownIcon sx={{ fontSize: 19 }} />
      </IconButton>
    </Box>
  );
};

const TableSettingsDialog: React.FC<TableSettingsDialogProps> = ({
  anchorEl,
  settings,
  viewMode = 'table',
  cards,
  title,
  onClose,
  onReset,
  onSave,
}) => {
  const theme = useTheme();
  const open = Boolean(anchorEl);
  // Available space between the bottom of the anchor (gear button) and
  // the bottom of the viewport. Clamps the popover so it never extends
  // past the viewport while staying glued to the anchor — without this,
  // MUI's auto-reposition flips a tall popover upward and the "open
  // under the button" promise breaks (visible in table mode where the
  // column drag list pushes the panel well over 500px tall).
  const [maxHeight, setMaxHeight] = useState(560);
  const [draft, setDraft] = useState<ResolvedTableSettings>(settings);
  const [draggedColumnKey, setDraggedColumnKey] = useState<string | null>(null);
  const draggedColumnKeyRef = useRef<string | null>(null);
  const orderedColumnsRef = useRef<TableColumnSettings[]>([]);

  // Sync draft with incoming settings whenever the popover opens.
  useEffect(() => {
    if (open) {
      setDraft(settings);
      setDraggedColumnKey(null);
      draggedColumnKeyRef.current = null;
    }
  }, [open, settings]);

  // Recompute the popover's maxHeight whenever it opens or the window
  // resizes. 16px viewport padding mirrors MUI's default marginThreshold.
  useEffect(() => {
    if (!open || !anchorEl) return;
    const compute = () => {
      const rect = anchorEl.getBoundingClientRect();
      const available = window.innerHeight - rect.bottom - 16;
      // 320 is the smallest height that still shows the header, one
      // section, and the footer without things collapsing oddly.
      setMaxHeight(Math.max(320, Math.min(560, available)));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [open, anchorEl]);

  // Live-preview: every draft mutation flushes to onSave. The prototype
  // explicitly forbids a save/cancel dance — the user expects changes to
  // land in the table behind the popover immediately.
  const persist = (next: ResolvedTableSettings) => {
    setDraft(next);
    void onSave(next);
  };

  const orderedColumns = useMemo(
    () => [...draft.columns].sort((a, b) => a.order - b.order),
    [draft.columns],
  );

  useEffect(() => {
    orderedColumnsRef.current = orderedColumns;
  }, [orderedColumns]);

  const visCount = orderedColumns.filter((c) => c.visible).length;
  const allVisible = visCount === orderedColumns.length;

  const updateColumns = (columns: TableColumnSettings[]) => {
    persist({
      ...draft,
      columns: columns.map((column, index) => ({
        ...column,
        order: index,
      })),
    });
  };

  const toggleColumn = (key: string) => {
    updateColumns(
      orderedColumns.map((column) =>
        column.key === key
          ? { ...column, visible: !column.visible }
          : column,
      ),
    );
  };

  const moveColumn = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= orderedColumns.length) return;
    const next = [...orderedColumns];
    [next[index], next[target]] = [next[target], next[index]];
    updateColumns(next);
  };

  const moveColumnToIndex = (sourceKey: string, targetIndex: number) => {
    const next = [...orderedColumns];
    const sourceIndex = next.findIndex((c) => c.key === sourceKey);
    if (
      sourceIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= next.length ||
      sourceIndex === targetIndex
    ) {
      return;
    }
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    updateColumns(next);
  };

  const showAllColumns = () => {
    updateColumns(orderedColumns.map((c) => ({ ...c, visible: true })));
  };

  const setPageSize = (n: number) => persist({ ...draft, pageSize: n });

  const finishPointerDrag = () => {
    draggedColumnKeyRef.current = null;
    setDraggedColumnKey(null);
    document.body.style.userSelect = '';
  };

  const handlePointerDown = (
    e: React.PointerEvent<HTMLElement>,
    columnKey: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    draggedColumnKeyRef.current = columnKey;
    setDraggedColumnKey(columnKey);
    document.body.style.userSelect = 'none';
  };

  const handlePointerMove = (e: PointerEvent) => {
    const sourceKey = draggedColumnKeyRef.current;
    if (!sourceKey) return;
    const targetEl = document
      .elementFromPoint(e.clientX, e.clientY)
      ?.closest<HTMLElement>('[data-table-column-key]');
    const targetKey = targetEl?.dataset.tableColumnKey;
    if (!targetKey || targetKey === sourceKey) return;
    const targetIndex = orderedColumnsRef.current.findIndex(
      (c) => c.key === targetKey,
    );
    moveColumnToIndex(sourceKey, targetIndex);
  };

  useEffect(() => {
    if (!draggedColumnKey) return;
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', finishPointerDrag);
    window.addEventListener('pointercancel', finishPointerDrag);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', finishPointerDrag);
      window.removeEventListener('pointercancel', finishPointerDrag);
    };
    // Intentionally keyed only on draggedColumnKey — listeners attach on drag
    // start and detach on drag end. The handlers read live values from refs
    // (draggedColumnKeyRef / orderedColumnsRef), so they never go stale;
    // adding them here would needlessly re-bind listeners on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggedColumnKey]);

  const handleReset = async () => {
    await onReset();
    onClose();
  };

  const headerTitle =
    title ??
    (viewMode === 'cards' ? 'Налаштування карток' : 'Налаштування таблиці');

  const activeColor = pickStatusColor('active', theme.palette.mode);
  const learnedColor = pickStatusColor('learned', theme.palette.mode);

  const inTable = viewMode === 'table';
  const showCardSections = viewMode === 'cards' && !!cards;

  // Cards-mode field toggles map to the same column-visibility flags as
  // table mode — the underlying setting is shared. Renders as flat
  // checkboxes (no drag/order, no count, since cards don't have layout
  // order). Looked up by key against `orderedColumns` so missing columns
  // are silently skipped.
  const cardFieldDefs: Array<{ key: string; label: string }> = [
    { key: 'transcription', label: 'Транскрипція' },
    { key: 'partOfSpeech', label: 'Частина мови' },
    { key: 'definition', label: 'Визначення' },
  ];
  const cardFields = cardFieldDefs
    .map((def) => {
      const col = orderedColumns.find((c) => c.key === def.key);
      return col ? { ...def, visible: col.visible } : null;
    })
    .filter(<T,>(v: T | null): v is T => v !== null);

  return (
    <Popover
      disableScrollLock
      keepMounted
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      // 8px gives MUI just enough room to nudge the popover back into
      // the viewport when the anchor sits near the right (or left)
      // edge — common on tablet/phone where the gear button is far
      // from the centre. Vertical overflow is handled by clamping
      // maxHeight (above), so MUI never needs to flip the popover up.
      marginThreshold={8}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2.5,
            boxShadow: '0 24px 60px -20px rgba(20,20,20,0.45)',
            display: 'flex',
            flexDirection: 'column',
            // Small offset so the popover doesn't kiss the button border.
            mt: 0.75,
            maxHeight,
            overflow: 'hidden',
            // Responsive width — 380px on tablet/desktop, full-viewport
            // (minus 16px breathing room) on phones so the panel never
            // hangs off-screen.
            width: { xs: 'calc(100vw - 16px)', sm: POPOVER_WIDTH },
          },
        },
      }}
    >
      {/* Header */}
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={1.25}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'primary.tableBorder',
          flex: 'none',
          px: 2,
          py: 1.75,
        }}
      >
        <TuneIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700 }}>
          {headerTitle}
        </Typography>
        <IconButton size={'small'} onClick={onClose}>
          <CloseIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        </IconButton>
      </Stack>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.75 }}>
        {/* Cards-only sections — replace the column drag list when in
            cards mode (and the consumer supplied `cards`). */}
        {showCardSections && cards && (
          <>
            <Box sx={{ mb: 2.25 }}>
              <GroupLabel>{'Карток у рядку'}</GroupLabel>
              <Stack direction={'row'} spacing={0.75}>
                {([1, 2, 3] as CardsPerRow[]).map((n) => {
                  const on = cards.cardsPerRow === n;
                  const Icon =
                    n === 1
                      ? ViewAgendaIcon
                      : n === 2
                      ? GridViewIcon
                      : ViewModuleIcon;
                  return (
                    <Box
                      key={n}
                      component={'button'}
                      onClick={() => cards.setCardsPerRow(n)}
                      sx={{
                        alignItems: 'center',
                        // `bgcolor` (not `background`) so the theme token
                        // resolves — `background: 'primary.main'` would be
                        // passed through as literal CSS and silently
                        // ignored by the browser.
                        bgcolor: on ? 'primary.main' : 'background.default',
                        border: '1px solid',
                        borderColor: on ? 'primary.main' : 'primary.tableBorder',
                        borderRadius: 1,
                        color: on ? 'common.white' : 'text.primary',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        flex: 1,
                        fontFamily: 'inherit',
                        fontSize: 14,
                        fontWeight: 700,
                        gap: 0.875,
                        height: 44,
                        justifyContent: 'center',
                        transition: 'all .14s ease',
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                      {n}
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <Box sx={{ mb: 2.25 }}>
              <GroupLabel>{'Поля картки'}</GroupLabel>
              <Stack spacing={0.25}>
                {cardFields.map((field) => (
                  <Box
                    key={field.key}
                    onClick={() => toggleColumn(field.key)}
                    sx={{
                      alignItems: 'center',
                      borderRadius: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      gap: 1.25,
                      height: 40,
                      px: 0.5,
                      '&:hover': { bgcolor: 'background.default' },
                    }}
                  >
                    <SquareCheckbox
                      checked={field.visible}
                      onClick={() => toggleColumn(field.key)}
                    />
                    <Typography
                      sx={{
                        color: field.visible ? 'text.primary' : 'text.secondary',
                        flex: 1,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {field.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </>
        )}

        {/* Columns — only meaningful in table mode */}
        {inTable && (
          <Box sx={{ mb: 2.25 }}>
            <GroupLabel
              right={
                <Box
                  component={'button'}
                  onClick={showAllColumns}
                  disabled={allVisible}
                  sx={{
                    background: 'transparent',
                    border: 'none',
                    color: allVisible ? 'text.secondary' : 'primary.main',
                    cursor: allVisible ? 'default' : 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 12.5,
                    fontWeight: 700,
                    opacity: allVisible ? 0.5 : 1,
                    p: 0,
                  }}
                >
                  {'Показати всі'}
                </Box>
              }
            >
              {`Колонки · ${visCount} з ${orderedColumns.length}`}
            </GroupLabel>
            <Stack spacing={0.25}>
              {orderedColumns.map((column, index) => (
                <ColumnRow
                  key={column.key}
                  column={column}
                  index={index}
                  total={orderedColumns.length}
                  draggable={draggedColumnKey === null || draggedColumnKey === column.key}
                  onToggle={() => toggleColumn(column.key)}
                  onMoveUp={() => moveColumn(index, -1)}
                  onMoveDown={() => moveColumn(index, 1)}
                  onPointerDown={(e) => handlePointerDown(e, column.key)}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Page size */}
        <Box sx={{ mb: 2.25 }}>
          <GroupLabel>{'Слів на сторінці'}</GroupLabel>
          <Stack direction={'row'} spacing={0.75}>
            {TABLE_PAGE_SIZE_OPTIONS.map((n) => {
              const on = draft.pageSize === n;
              return (
                <Box
                  key={n}
                  component={'button'}
                  onClick={() => setPageSize(n)}
                  sx={{
                    // Same trap as the cards-per-row buttons above —
                    // `background` shorthand swallows theme keys, must
                    // use `bgcolor` for primary.main to actually paint.
                    bgcolor: on ? 'primary.main' : 'background.default',
                    border: '1px solid',
                    borderColor: on ? 'primary.main' : 'primary.tableBorder',
                    borderRadius: 1,
                    color: on ? 'common.white' : 'text.primary',
                    cursor: 'pointer',
                    flex: 1,
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: 700,
                    height: 38,
                    transition: 'all .14s ease',
                  }}
                >
                  {n}
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Highlight */}
        <Box>
          <GroupLabel>{'Підсвітка рядків'}</GroupLabel>
          <Stack spacing={0.25}>
            {[
              {
                key: 'highlightActiveSpeech' as const,
                color: activeColor,
                meta: STATUS_META.active,
                checked: draft.highlightActiveSpeech,
                onChange: () =>
                  persist({
                    ...draft,
                    highlightActiveSpeech: !draft.highlightActiveSpeech,
                  }),
              },
              {
                key: 'highlightLearned' as const,
                color: learnedColor,
                meta: STATUS_META.learned,
                checked: draft.highlightLearned,
                onChange: () =>
                  persist({
                    ...draft,
                    highlightLearned: !draft.highlightLearned,
                  }),
              },
            ].map((row) => (
              <Stack
                key={row.key}
                direction={'row'}
                alignItems={'center'}
                spacing={1.25}
                sx={{ height: 42 }}
              >
                <Box
                  sx={{
                    bgcolor: row.color,
                    borderRadius: '50%',
                    flex: 'none',
                    height: 11,
                    width: 11,
                  }}
                />
                <Typography sx={{ flex: 1, fontSize: 14 }}>
                  <Box component={'span'} sx={{ fontWeight: 700 }}>
                    {row.meta.label}
                  </Box>
                  <Box component={'span'} sx={{ color: 'text.secondary' }}>
                    {` — ${row.meta.desc}`}
                  </Box>
                </Typography>
                <StatusSwitch
                  on={row.checked}
                  color={row.color}
                  onClick={row.onChange}
                />
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Footer */}
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{
          borderTop: '1px solid',
          borderColor: 'primary.tableBorder',
          flex: 'none',
          gap: 1.5,
          px: 2,
          py: 1.25,
        }}
      >
        <Box
          component={'button'}
          onClick={handleReset}
          sx={{
            alignItems: 'center',
            background: 'transparent',
            border: 'none',
            color: 'text.secondary',
            cursor: 'pointer',
            display: 'inline-flex',
            fontFamily: 'inherit',
            fontSize: 13.5,
            fontWeight: 600,
            gap: 0.5,
            p: 0,
          }}
        >
          <RestartAltIcon sx={{ fontSize: 18 }} />
          {'Скинути до типових'}
        </Box>
        <Button
          variant={'contained'}
          size={'small'}
          onClick={onClose}
          sx={{ height: 34, px: 2 }}
        >
          {'Готово'}
        </Button>
      </Stack>
    </Popover>
  );
};

export default TableSettingsDialog;
